import React, { useMemo } from "react";

import ActionItemIcon from "@uui/CheckCircleBroken";
import QuestionIcon from "@uui/HelpCircle";
import TakeawaysIcon from "@uui/MessageTextSquare02";
import { find, isEmpty, orderBy } from "lodash/fp";
import { shallow } from "zustand/shallow";

import IconHeading from "@app/analytics/components/IconHeading";
import KeyQuestionAnswer from "@app/analytics/components/KeyQuestionAnswer";
import useTopicReport from "@app/analytics/TopicReport/hooks/useTopicReport";
import useTopicReportEntities from "@app/analytics/TopicReport/hooks/useTopicReportEntities";
import useTopicReportStore from "@app/analytics/TopicReport/hooks/useTopicReportStore";
import type { TopicReportTakeawaysSortOrder } from "@app/analytics/TopicReport/types";
import { getEntityCopyContent } from "@app/analytics/TopicReport/utils";
import type { TopicEntity } from "@app/analytics/types";

import ExpandableList from "@rui/ExpandableList";
import { Column, Row } from "@rui/Stack";

import Citation from "./Citation";
import { ListItem, Takeaway } from "./styles";
import TakeawaysSortMenu from "./TakeawaysSortMenu";
import { EmptySummaryText, SummaryText } from "../styles";

const MAX_COLLAPSED_LIST_ITEMS = 4;

const sortFn = (sortOrder: TopicReportTakeawaysSortOrder) => {
  if (sortOrder === "rank") return "rank";
  return (entity: TopicEntity) => entity.endTime.toMillis();
};

const ActionItem = ({
  actionItem,
  ...props
}: {
  copyContent?: string;
  actionItem: TopicEntity;
  onClick?: () => void;
}) => {
  return (
    <Takeaway {...props}>
      <ListItem>
        <Row spacing={1}>
          <ActionItemIcon className="list-item__icon" />
          <SummaryText>{actionItem.text}</SummaryText>
        </Row>
        <Citation entity={actionItem} />
      </ListItem>
    </Takeaway>
  );
};

const KeyQuestion = ({
  question,
  ...props
}: {
  copyContent?: string;
  question: TopicEntity;
  onClick?: () => void;
}) => {
  const answer = find({ kind: "key_question_answer" }, question.children);
  return (
    <Takeaway spacing={{ xxs: 1, sm: 0.5 }} {...props}>
      <ListItem spacing={{ xxs: 1, sm: 0 }}>
        <Row spacing={1}>
          <QuestionIcon className="list-item__icon" />
          <SummaryText>{question.text}</SummaryText>
        </Row>
        <Citation entity={question} />
      </ListItem>
      {answer && (
        <Column paddingBottom={0.5} marginLeft="28px">
          <KeyQuestionAnswer answer={answer.text} />
        </Column>
      )}
    </Takeaway>
  );
};

const Takeaways = () => {
  const { isSharedReport } = useTopicReport();
  const { actionItems, keyQuestions } = useTopicReportEntities();

  const { sortOrder, setSelectedEntityId } = useTopicReportStore(
    (state) => ({
      sortOrder: state.takeawaysSortOrder,
      setSelectedEntityId: state.setSelectedEntityId,
    }),
    shallow
  );

  const sortedEntities = useMemo(() => {
    return orderBy(sortFn(sortOrder), "desc", [
      ...actionItems,
      ...keyQuestions,
    ]);
  }, [actionItems.length, keyQuestions.length, sortOrder]);

  const hasNoTakeaways = isEmpty(sortedEntities);

  return (
    <Column spacing={1}>
      <Row variant="apart" spacing={2} minHeight="34px">
        <IconHeading
          color="shades.ghost.500"
          startIcon={<TakeawaysIcon sx={{ color: "shades.primary.500" }} />}
        >
          Takeaways
        </IconHeading>
        {!hasNoTakeaways && (
          <TakeawaysSortMenu disabled={sortedEntities.length < 2} />
        )}
      </Row>
      {hasNoTakeaways ? (
        <EmptySummaryText>No takeaways</EmptySummaryText>
      ) : (
        <ExpandableList
          max={MAX_COLLAPSED_LIST_ITEMS}
          buttonProps={{
            sx: { marginLeft: "20px", marginTop: "8px" },
            viewMoreLabel: `View ${
              sortedEntities.length - MAX_COLLAPSED_LIST_ITEMS
            } more`,
            viewLessLabel: "View less",
          }}
        >
          {sortedEntities.map((entity) => {
            const handleClick = isSharedReport
              ? undefined
              : () => setSelectedEntityId(entity.entityId);
            const copyContent = isSharedReport
              ? undefined
              : getEntityCopyContent(entity);

            if (entity.kind === "action_item") {
              return (
                <ActionItem
                  key={entity.entityId}
                  actionItem={entity}
                  copyContent={copyContent}
                  onClick={handleClick}
                />
              );
            }

            return (
              <KeyQuestion
                key={entity.entityId}
                copyContent={copyContent}
                question={entity}
                onClick={handleClick}
              />
            );
          })}
        </ExpandableList>
      )}
    </Column>
  );
};

export default Takeaways;
